package components.auth

import androidx.compose.runtime.*
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import design.AccessibilityGuidelines
import design.DesignTokens
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet

/**
 * Registration Step 3: Account Creation
 * Username and password input with real-time validation
 */
@Composable
fun RegistrationStep3_Account(
    email: String,
    username: String,
    password: String,
    onUsernameChange: (String) -> Unit,
    onPasswordChange: (String) -> Unit,
    onComplete: () -> Unit,
    onBack: () -> Unit,
    isLoading: Boolean,
    error: String?,
    authViewModel: AuthViewModel
) {
    var passwordError by remember { mutableStateOf<String?>(null) }
    var usernameError by remember { mutableStateOf<String?>(null) }
    var passwordVisible by remember { mutableStateOf(false) }
    var confirmPassword by remember { mutableStateOf("") }
    var confirmPasswordError by remember { mutableStateOf<String?>(null) }

    val authState by authViewModel.state.collectAsState()
    val coroutineScope = rememberCoroutineScope()

    // Username availability check with debounce
    LaunchedEffect(username) {
        if (username.length >= 3) {
            kotlinx.coroutines.delay(500) // 500ms debounce
            if (username.length >= 3) {
                authViewModel.onEvent(AuthEvent.OnCheckUsername(username))
            }
        } else {
            // Reset state when username is too short
            usernameError = when {
                username.isEmpty() -> null
                username.length < 3 -> "ユーザー名は3文字以上で入力してください"
                else -> null
            }
        }
    }

    // Password validation
    val passwordRequirements = remember(password) {
        getPasswordRequirements(password)
    }
    val isPasswordValid = remember(password) {
        password.length >= 8 &&
        password.any { it.isUpperCase() } &&
        password.any { it.isLowerCase() } &&
        password.any { it.isDigit() } &&
        password.any { !it.isLetterOrDigit() }
    }

    // Username format validation
    val isValidUsernameFormat = remember(username) {
        username.matches(Regex("^[a-zA-Z0-9_-]+$")) && username.length in 3..20
    }

    // Announce errors to screen readers
    LaunchedEffect(error) {
        if (error != null) {
            AccessibilityGuidelines.announceError(error!!)
        }
    }

    LaunchedEffect(usernameError) {
        if (usernameError != null) {
            AccessibilityGuidelines.announceError(usernameError!!)
        }
    }

    LaunchedEffect(passwordError) {
        if (passwordError != null) {
            AccessibilityGuidelines.announceError(passwordError!!)
        }
    }

    LaunchedEffect(confirmPasswordError) {
        if (confirmPasswordError != null) {
            AccessibilityGuidelines.announceError(confirmPasswordError!!)
        }
    }

    Div({ classes(AppStyleSheet.loginContainer) }) {
        Div({ classes(AppStyleSheet.loginForm) }) {
            H2({ classes(AppStyleSheet.loginTitle) }) {
                Text("アカウントを作成")
            }

            P({
                style {
                    property("color", DesignTokens.Colors.textSecondary.toString())
                    property("margin-bottom", "24px")
                    property("text-align", "center")
                }
            }) {
                Text("「$email」でアカウントを作成します")
            }

            // Username input
            Div({ classes(AppStyleSheet.formGroup) }) {
                Label("username-input") {
                    Text("ユーザー名")
                }

                val usernameInputClasses = buildList {
                    add(AppStyleSheet.textInput)
                    if (usernameError != null) add(AppStyleSheet.inputInvalid)
                    else if (isValidUsernameFormat && authState.usernameAvailable == true) add(AppStyleSheet.inputValid)
                }

                Input(type = InputType.Text) {
                    id("username-input")
                    classes(*usernameInputClasses.toTypedArray())
                    value(username)
                    attr("autocomplete", "username")
                    attr("required", "true")
                    attr("minlength", "3")
                    attr("maxlength", "20")
                    attr("pattern", "^[a-zA-Z0-9_-]+$")
                    attr("aria-invalid", if (usernameError != null || authState.usernameAvailable == false) "true" else "false")
                    attr("aria-describedby", "username-hint")
                    onInput {
                        onUsernameChange(it.value)
                        usernameError = null
                    }
                    if (isLoading) {
                        disabled()
                    }
                }

                // Username availability indicator
                Div({
                    id("username-hint")
                    style {
                        property("margin-top", "8px")
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "8px")
                        property("font-size", "14px")
                    }
                }) {
                    when {
                        authState.usernameChecking -> {
                            // Loading spinner
                            Span({
                                style {
                                    property("display", "inline-block")
                                    property("width", "14px")
                                    property("height", "14px")
                                    property("border", "2px solid #f0f0f0")
                                    property("border-top", "2px solid " + DesignTokens.Colors.primary.toString())
                                    property("border-radius", "50%")
                                }
                            }) {}
                            Text("確認中...")
                        }
                        username.isNotEmpty() && username.length >= 3 && authState.usernameAvailable == true -> {
                            Span({
                                style {
                                    property("color", DesignTokens.Colors.success.toString())
                                }
                            }) { Text("✓ 使用可能") }
                        }
                        username.isNotEmpty() && username.length >= 3 && authState.usernameAvailable == false -> {
                            Span({
                                style {
                                    property("color", DesignTokens.Colors.danger.toString())
                                }
                            }) { Text("✕ 既に使用されています") }
                        }
                        usernameError != null -> {
                            val usernameErrorMsg = usernameError!!
                            Span({
                                classes(AppStyleSheet.inputHint)
                                style {
                                    property("color", DesignTokens.Colors.danger.toString())
                                }
                            }) {
                                Text(usernameErrorMsg)
                            }
                        }
                        else -> {
                            Span({
                                classes(AppStyleSheet.inputHint)
                            }) {
                                Text("3〜20文字の英数字、ハイフン、アンダースコア")
                            }
                        }
                    }
                }
            }

            // Password input
            Div({ classes(AppStyleSheet.formGroup) }) {
                Label("password-input") {
                    Text("パスワード")
                }

                val passwordInputClasses = buildList {
                    add(AppStyleSheet.textInput)
                    if (passwordError != null) add(AppStyleSheet.inputInvalid)
                    else if (isPasswordValid) add(AppStyleSheet.inputValid)
                }

                Div({
                    style {
                        property("position", "relative")
                    }
                }) {
                    Input(type = if (passwordVisible) InputType.Text else InputType.Password) {
                        id("password-input")
                        classes(*passwordInputClasses.toTypedArray())
                        value(password)
                        attr("autocomplete", "new-password")
                        attr("required", "true")
                        attr("minlength", "8")
                        attr("aria-invalid", if (passwordError != null) "true" else "false")
                        attr("aria-describedby", "password-hint")
                        style {
                            property("padding-right", "48px") // space for toggle button
                        }
                        onInput {
                            onPasswordChange(it.value)
                            passwordError = null
                        }
                        if (isLoading) {
                            disabled()
                        }
                    }

                    // Password visibility toggle
                    Button({
                        style {
                            property("position", "absolute")
                            property("right", "8px")
                            property("top", "50%")
                            property("transform", "translateY(-50%)")
                            property("background", "none")
                            property("border", "none")
                            property("cursor", "pointer")
                            property("padding", "8px")
                            property("color", DesignTokens.Colors.textSecondary.toString())
                            property("font-size", "18px")
                        }
                        attr("type", "button")
                        attr("aria-label", if (passwordVisible) "パスワードを非表示" else "パスワードを表示")
                        onClick {
                            passwordVisible = !passwordVisible
                        }
                    }) {
                        Text(if (passwordVisible) "🙈" else "👁️")
                    }
                }

                // Password strength meter
                if (password.isNotEmpty()) {
                    PasswordStrengthMeter(password, showRequirements = true)
                }
            }

            // Confirm password input
            Div({ classes(AppStyleSheet.formGroup) }) {
                Label("confirm-password-input") {
                    Text("パスワード（確認）")
                }

                val confirmPasswordInputClasses = buildList {
                    add(AppStyleSheet.textInput)
                    if (confirmPasswordError != null) add(AppStyleSheet.inputInvalid)
                    else if (confirmPassword.isNotEmpty() && confirmPassword == password) add(AppStyleSheet.inputValid)
                }

                Input(type = if (passwordVisible) InputType.Text else InputType.Password) {
                    id("confirm-password-input")
                    classes(*confirmPasswordInputClasses.toTypedArray())
                    value(confirmPassword)
                    attr("autocomplete", "new-password")
                    attr("required", "true")
                    attr("aria-invalid", if (confirmPasswordError != null) "true" else "false")
                    attr("aria-describedby", "confirm-password-hint")
                    onInput {
                        confirmPassword = it.value
                        confirmPasswordError = when {
                            it.value.isEmpty() -> null
                            it.value != password -> "パスワードが一致しません"
                            else -> null
                        }
                    }
                    if (isLoading) {
                        disabled()
                    }
                }

                // Confirm password hint
                if (confirmPasswordError != null) {
                    val confirmPwdError = confirmPasswordError!!
                    P({
                        id("confirm-password-hint")
                        classes(AppStyleSheet.inputHint)
                        style {
                            property("color", DesignTokens.Colors.danger.toString())
                        }
                    }) {
                        Text(confirmPwdError)
                    }
                }
            }

            // Error messages
            if (error != null) {
                val errorMsg = error!!
                Div({
                    classes(AppStyleSheet.formError)
                    attr("role", "alert")
                    attr("aria-live", "assertive")
                }) {
                    Text(errorMsg)
                }
            }

            // Buttons
            Div({ classes(AppStyleSheet.loginButtonGroup) }) {
                Button({
                    classes(AppStyleSheet.primaryButton)
                    if (!isValidUsernameFormat || !isPasswordValid || confirmPassword != password || authState.usernameAvailable != true || isLoading) attr("disabled", "true")
                    onClick {
                        // Validate all fields
                        usernameError = when {
                            username.isEmpty() -> "ユーザー名を入力してください"
                            !isValidUsernameFormat -> "ユーザー名の形式が正しくありません"
                            authState.usernameAvailable == false -> "このユーザー名は既に使用されています"
                            else -> null
                        }

                        passwordError = when {
                            password.isEmpty() -> "パスワードを入力してください"
                            !isPasswordValid -> "パスワードが要件を満たしていません"
                            else -> null
                        }

                        confirmPasswordError = when {
                            confirmPassword.isEmpty() -> "確認用パスワードを入力してください"
                            confirmPassword != password -> "パスワードが一致しません"
                            else -> null
                        }

                        if (usernameError == null && passwordError == null && confirmPasswordError == null) {
                            onComplete()
                        }
                    }
                }) {
                    Text(if (isLoading) "作成中..." else "アカウントを作成")
                }

                Button({
                    classes(AppStyleSheet.secondaryButton)
                    onClick { onBack() }
                    if (isLoading) disabled()
                }) {
                    Text("戻る")
                }
            }

            // Help text
            P({
                style {
                    property("margin-top", "16px")
                    property("font-size", "14px")
                    property("color", DesignTokens.Colors.textSecondary.toString())
                    property("text-align", "center")
                }
            }) {
                Text("パスワードは安全に暗号化されて保存されます")
            }
        }
    }
}
